import React, { Component } from 'react'
import styles from './CallWindow.module.css'
import ButtonRound from '../Button/ButtonRound'
import IconMicrophone from '../../assets/icons/IconMicrophone'
import IconEndCall from '../../assets/icons/IconEndCall'
import ReactTooltip from 'react-tooltip'
import IconChatMinimize from '../../assets/icons/IconChatMinimize'
import defaultProfilePic from '../../assets/images/default-profile.svg'
import Draggable from 'react-draggable'
import { GroChatVoipContext } from '../GroChatVoipProvider/GroChatVoipProvider'
import PropTypes from 'prop-types'
import { secondsToTime } from '../../util/date'
import IconMutedMicrophone from '../../assets/icons/IconMutedMicrophone'
import API_CHAT from '../../api/apiChat'
import { withRouter } from 'react-router'
import { connect } from 'react-redux'
import { getContent } from '../../util/responseUtil'

export const CallContext = React.createContext()

/**
 * @extends {React.Component<CallWindow.propTypes>}
 */
class CallWindow extends Component {
    constructor(props) {
        super(props)

        /**
         * @type {React.RefObject<HTMLDivElement>}
         */
        this.callWindowRef = React.createRef()
        this.state = {
            isMinimized: false,
            isDragging: false,
            position: {
                x: 0,
                y: 0
            },
            dragOffset: {
                x: 0,
                y: 0
            },
            userToCall: {
                id: 0,
                name: '',
                phone: '',
                image: ''
            }
        }
    }

    componentDidMount() {
        this.setDefaultPosition()

        const ctx = this.getGroChatVoipContext()

        ctx.onCall(this.handleOnCall)
        ctx.onCallEnded(this.handleOnCallEnded)

        window.addEventListener('resize', () => {
            if (!this.callWindowRef.current) {
                return
            }
            
            let x = this.state.position.x
            let y = this.state.position.y

            if ((x + this.callWindowRef.current.clientWidth) >= window.innerWidth) {
                x = window.innerWidth - this.callWindowRef.current.clientWidth
            }

            if ((y + this.callWindowRef.current.clientHeight) >= window.innerHeight) {
                y = window.innerHeight - this.callWindowRef.current.clientHeight
            }

            this.setState({
                position: {
                    x,
                    y
                } 
            })
        })
    }
    
    setDefaultPosition = () => {
        if (!this.callWindowRef.current) {
            return
        }

        const callWindow = this.callWindowRef.current
        const callWindowWidth = callWindow.clientWidth
        const callWindowHeight = callWindow.clientHeight

        this.setState({
            position: {
                x: (window.innerWidth / 2) - (callWindowWidth / 2),
                y: (window.innerHeight / 2) - (callWindowHeight / 2)
            }
        })
    }

    handleOnCall = async (currentCall) => {
        let clientId = currentCall.callFrom
        const isMe = currentCall.callFrom === this.props.user.client_id

        if (isMe) {
            clientId = currentCall.callTo
        }
        
        try {
            const profile = await this.getUserProfile(clientId)
            
            this.setState({
                userToCall: {
                    id: profile.id,
                    name: profile.name,
                    phone: profile.phone,
                    image: profile.image
                }
            })
        } catch(err) {
            return console.error(err)
        }
    }

    handleOnCallEnded = (endCall, currentCall) => {
        this.uploadCallHistory(currentCall)

        setTimeout(() => {
            endCall()
            
            this.setState({ isMinimized: false }, () => {
                this.setDefaultPosition()
            })
        }, 1000)
    }

    uploadCallHistory = async (currentCall) => {
        const ctx = this.getGroChatVoipContext()
        const formData = new FormData()
        
        let duration = secondsToTime(currentCall.duration) 

        if (duration.split(':').length === 2) {
            duration = '00:' + duration
        }

        const data = {
            call_number_outgoing : this.props.user.nexcare_id,
            call_number_incoming : this.state.userToCall.id,
            call_status : this.getCallLogStatus(currentCall),
            ticket_no : ctx.getContext("ticketNo"),
            call_at : currentCall.start_at,
            close_at : currentCall.end_at,
            duration
        }

        formData.append('content', JSON.stringify(data))
        if (currentCall.recordingFile) {
            formData.append('photo1', currentCall.recordingFile)
        }

        try {
            await API_CHAT.postCallHistory(formData, this.props.token)
        } catch {
            this.handleDownload(currentCall.recordingFile)
        }
    }

    /**
     * @returns {string}
     */
    getCallLogStatus = (currentCall) => {
        const endType = currentCall.end_type

        switch(endType) {
            case "end" :
                return "Keluar"
            case "missed" : 
                return "Tak Terjawab"
            case "reject" : 
                return "Tak Terjawab"
            default :
                return ''
        }
    }

    /**
     * @param {File} recordingFile
     */
    handleDownload = (recordingFile) => {
        if (!recordingFile) {
            return
        }

        const url = window.URL.createObjectURL(recordingFile)
        const a = document.createElement('a')

        a.href = url
        a.download = recordingFile.name
        a.click()

        window.URL.revokeObjectURL(url)
    }

    handleStart = (e) => {
        this.setState({
            isDragging: true,
            dragOffset: {
                x: e.clientX - this.state.position.x,
                y: e.clientY - this.state.position.y,
            }
        })
    }

    handleDrag = (e) => {
        if (!this.state.isDragging) {
            return
        }

        let x = e.clientX - this.state.dragOffset.x
        let y = e.clientY - this.state.dragOffset.y 
        
        /*
            Left overflow
        */
        if (x <= 0) {
            x = 0
        }

        /*
            Right overflow
        */
        if ((x + this.callWindowRef.current.clientWidth) >= window.innerWidth) {
            x = window.innerWidth - this.callWindowRef.current.clientWidth
        }

        /*
            Top overflow
        */
        if (y <= 0) {
            y = 0
        }

        /*
            Bottom overflow
        */
        if ((y + this.callWindowRef.current.clientHeight) >= window.innerHeight) {
            y = window.innerHeight - this.callWindowRef.current.clientHeight
        }

        this.setState({
            position: {
                x,
                y
            }
        })
    }

    handleStop = () => {
        this.setState({
            isDragging: false,
        })
    }

    handleMinimize = () => {
        this.setState({
            isMinimized: !this.state.isMinimized
        })
    }

    /**
     * @returns {React.ContextType<GroChatVoipContext>} 
     */
    getGroChatVoipContext = () => {
        return this.context
    }

    /**
     * @returns {{ id: number, name: string, phone: string, image: string }}
     */
    getUserProfile = async (clientId = '') => {
        const res = await API_CHAT.getUserByClientId(clientId, this.props.token)
        const data = getContent(res)

        return {
            id:    data.id,
            name:  `${data.first_name} ${data.last_name}`,
            phone: data.phone,
            image: defaultProfilePic
        }
    }

    /**
     * @returns {string}
     */
    getCallStatus = () => {
        const ctx = this.getGroChatVoipContext()

        if (ctx.isCalling) {
            return 'Memanggil'
        }

        if (ctx.isRinging) {
            return 'Berdering'
        }

        if (ctx.isOnCall) {
            return secondsToTime(ctx.duration)
        }

        if (ctx.isCallEnded) {
            return 'Panggilan Berakhir'
        }

        return ''
    }

    /**
     * @param {MouseEvent} e 
     */
    handleEndCall = (e) => {
        e.preventDefault()

        const ctx = this.getGroChatVoipContext()
        ctx.hangUp()
    }

    render() {
        const windowSizeClass = this.state.isMinimized ? styles['call-window-sm'] : styles['call-window-lg']
        const ctx = this.getGroChatVoipContext()
        const call = ctx.getCall()

        return (
            <>
                <ReactTooltip
                    id="callwindow"
                    place="top"
                    type="info"
                    effect="solid"
                />
                <Draggable 
                    bounds="parent" 
                    position={this.state.position} 
                    onStart={this.handleStart} 
                    onDrag={this.handleDrag} 
                    onStop={this.handleStop} 
                    handle=".handle"
                >
                    <div 
                        className={`${styles['call-window']} ${call ? styles['show'] : ''} ${windowSizeClass} handle`} 
                        ref={this.callWindowRef}
                    >
                        <div className={`${styles['header']} bg-chat-header-window flex justify-between items-center p-5`}>
                            <p className="text-2xl text-white font-medium">Telepon</p>
                            <div
                                className="cursor-pointer"
                                data-tip="Kecilkan"
                                data-for="callwindow"
                                onClick={this.handleMinimize}
                            >
                                <IconChatMinimize />
                            </div>
                        </div>
                        <div className={`${styles['call']} text-white`}>
                            <div className={`${styles['profile']}`} onDoubleClick={this.state.isMinimized ? this.handleMinimize : undefined}>
                                <div className={`${styles['profile-img']} border-white rounded-full overflow-hidden`}>
                                    <img className="w-full h-full object-cover pointer-events-none" src={this.state.userToCall.image} alt={this.state.userToCall.name}  />
                                </div>
                                <div className={`${styles['profile-info']}`}>
                                    <strong className={`${styles['profile-name']} font-bold`}>{this.state.userToCall.name}</strong>
                                    <p className={`${styles['profile-phone']} mb-3`}>{this.state.userToCall.phone}</p>
                                    <p className={`${styles['time']}`}>{this.getCallStatus()}</p>
                                </div>
                            </div>
                            <div className={`${styles['cta']}`}>
                                <ButtonRound 
                                    disabled={ctx.isCallEnded} 
                                    className="bg-nexcareblue2" 
                                    onClick={ctx.toggleVoice}
                                >
                                    { 
                                        ctx.isMuted 
                                            ? <IconMutedMicrophone />
                                            : <IconMicrophone />
                                    }
                                </ButtonRound>
                                <ButtonRound 
                                    disabled={ctx.isCallEnded} 
                                    className="bg-nexcarered2" 
                                    onClick={this.handleEndCall}
                                >
                                    <IconEndCall />
                                </ButtonRound>
                            </div>
                        </div>
                    </div>
                </Draggable>
            </>
        )
    }
}

CallWindow.contextType = GroChatVoipContext
CallWindow.propTypes = {
    /**
     * @type {string}
     */
    clientId: PropTypes.string.isRequired
}

const mapStateToProps = (state) => {
    return {
        token: state.authentication.token,
        user: state.authentication.userDetail
    };
};

export default withRouter(connect(mapStateToProps)(CallWindow));